/**
 * creature/skeleton.ts — The Bones of Every Living Thing
 *
 * A joint tree defines the skeleton of any creature — cat, dog, horse, dragon.
 * Every joint knows its parent, its rest angle, its constraints. At runtime,
 * a SkeletonInstance holds the solved screen-space positions of every joint,
 * updated each frame by the FK solver.
 *
 * This is the generalized form of the title.ts cat skeleton:
 * hip -> spine -> shoulder -> neck -> head, hip -> tail, shoulder -> legs.
 * But now it works for any body plan.
 */

// ─── Joint Definition ───────────────────────────────────────────────────────

/** A single joint in the skeleton tree */
export interface Joint {
  /** Unique identifier: "hip", "spine", "shoulder", "neck", "head", "tail_0" etc. */
  id: string;
  /** Parent joint ID. null = root joint */
  parentId: string | null;
  /** Rest offset from parent in local space (world units, pre-scale) */
  restOffset: { x: number; y: number };
  /** Resting angle in radians relative to parent bone direction */
  restAngle: number;
  /** Constraint: minimum angle deviation from rest */
  minAngle: number;
  /** Constraint: maximum angle deviation from rest */
  maxAngle: number;
  /** Bone length from this joint to next joint / end effector (world units) */
  length: number;
  /** Tags for semantic queries: "leg_front_l", "ear_r", "tail", "wing_l" etc. */
  tags: string[];
}

// ─── IK Chain Definition ────────────────────────────────────────────────────

/** Defines an inverse kinematics chain for foot planting or look-at */
export interface IKChainDef {
  /** Chain identifier: "foot_front_l", "look_at" */
  id: string;
  /** Ordered joint IDs from root to end effector */
  jointIds: string[];
  /** Target mode: "ground" snaps to floor, "point" aims at coordinates */
  targetMode: "ground" | "point";
  /** FABRIK iterations (2-4 is sufficient for small chains) */
  iterations: number;
  /** Convergence tolerance in world units */
  tolerance: number;
}

// ─── Draw Group ─────────────────────────────────────────────────────────────

/**
 * A named group of body parts drawn together in painter's order.
 * Each group has a name (for the renderer to switch on) and the joint IDs
 * it needs access to for drawing.
 */
export interface DrawGroup {
  /** Semantic name: "back_legs", "tail", "body", "front_legs", "head", "mane" */
  name: string;
  /** Joint IDs this group reads from for positioning */
  joints: string[];
}

// ─── Skeleton Definition ────────────────────────────────────────────────────

/**
 * The skeleton definition — shared by all instances of a species.
 * This is pure data: joint tree, draw order, IK chains.
 * Created once per species, referenced by all creature instances.
 */
export interface SkeletonDef {
  /** Species skeleton ID: "cat_small", "horse_standard", "wolf" */
  id: string;
  /** Joints ordered root-first (parent always before children) */
  joints: Joint[];
  /** Root joint ID (typically "hip") */
  rootJointId: string;
  /**
   * Draw groups in painter's algorithm order (back to front).
   * e.g. [back_legs, tail, body, front_legs, head]
   */
  drawOrder: DrawGroup[];
  /** IK chain definitions for foot planting and look-at */
  ikChains: IKChainDef[];
}

// ─── Runtime Instance ───────────────────────────────────────────────────────

/**
 * A runtime skeleton instance — one per living creature.
 * Holds the current solved screen-space positions and angles,
 * updated each frame by the FK solver.
 */
export interface SkeletonInstance {
  /** Reference to the shared definition */
  def: SkeletonDef;
  /** Current screen-space positions per joint (set by solveFK) */
  jointPositions: Map<string, { x: number; y: number }>;
  /** Current angles per joint in radians (modified by pose blending + IK) */
  jointAngles: Map<string, number>;
}

// ─── Factory ────────────────────────────────────────────────────────────────

/** Create a runtime instance from a skeleton definition */
export function createSkeletonInstance(def: SkeletonDef): SkeletonInstance {
  const jointPositions = new Map<string, { x: number; y: number }>();
  const jointAngles = new Map<string, number>();

  for (const joint of def.joints) {
    jointPositions.set(joint.id, { x: 0, y: 0 });
    jointAngles.set(joint.id, joint.restAngle);
  }

  return { def, jointPositions, jointAngles };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Find a joint by ID in a skeleton definition */
export function getJoint(def: SkeletonDef, id: string): Joint | undefined {
  return def.joints.find((j) => j.id === id);
}

/** Find all joints with a given tag */
export function getJointsByTag(def: SkeletonDef, tag: string): Joint[] {
  return def.joints.filter((j) => j.tags.includes(tag));
}

/** Get the parent chain from a joint back to the root */
export function getAncestorChain(def: SkeletonDef, jointId: string): string[] {
  const chain: string[] = [];
  let current = jointId;
  const jointMap = new Map(def.joints.map((j) => [j.id, j]));
  while (current) {
    chain.push(current);
    const joint = jointMap.get(current);
    if (!joint || !joint.parentId) break;
    current = joint.parentId;
  }
  return chain.reverse(); // root-first
}

/** Get immediate children of a joint */
export function getChildren(def: SkeletonDef, parentId: string): Joint[] {
  return def.joints.filter((j) => j.parentId === parentId);
}
